import { useEffect, useState } from "react";
import { toast } from "sonner";
import Cell from "@/components/quote/Cell";
import api, { apiErr } from "@/lib/api";
import { Wrench, Plus, Trash2, Save, Loader2, ListChecks, Ship, Table2, GripVertical } from "lucide-react";
import { CHAR_GROUPS, groupOf } from "@/lib/charGroups";

const IMPORT_FIELDS = [
  ["Flete x contenedor ($)", "flete_per_container", 1],
  ["Préstamo contenedor ($)", "prestamo_container", 1],
  ["Seguro (tasa)", "seguro_rate", 0.001],
  ["Impuestos (tasa)", "impuestos_rate", 0.01],
  ["Percepción IGV (tasa)", "percepcion_rate", 0.001],
  ["Comisión agencia (tasa)", "comision_rate", 0.001],
  ["Gastos operativos ($)", "gastos_operativos", 1],
  ["Capacidad contenedor (und)", "capacidad_contenedor", 1],
  ["Traslado Callao-Magdalena ($)", "traslado", 1],
  ["Desperdicio (tasa)", "desperdicio_rate", 0.01],
];

export default function MantenimientoTab({ importParams, onImport, solesParams, onSolesParam, onCatalogSaved, globalOnly }) {
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refs, setRefs] = useState(null);
  const [savingRefs, setSavingRefs] = useState(false);
  const [draggedRid, setDraggedRid] = useState(null);

  const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

  useEffect(() => {
    api.get("/characteristics")
      .then((r) => setCatalog(r.data.map((c) => ({ ...c, _rid: uid(), group: groupOf(c) }))))
      .catch((e) => toast.error(apiErr(e)));
    api.get("/settings/references").then((r) => setRefs(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const setItemById = (rid, field, v) => setCatalog((c) => c.map((it) => it._rid === rid ? { ...it, [field]: v } : it));
  const addItemToGroup = (group) => setCatalog((c) => [...c, { _rid: uid(), name: "", price: 0, group }]);
  const removeItemById = (rid) => setCatalog((c) => c.filter((it) => it._rid !== rid));

  // Drop on an empty section / header: append to the END of that group.
  const moveToGroup = (rid, group) => setCatalog((c) => {
    const dragged = c.find((x) => x._rid === rid);
    if (!dragged) return c;
    const rest = c.filter((x) => x._rid !== rid);
    let lastIdx = -1;
    rest.forEach((x, i) => { if (groupOf(x) === group) lastIdx = i; });
    const pos = lastIdx >= 0 ? lastIdx + 1 : rest.length;
    return [...rest.slice(0, pos), { ...dragged, group }, ...rest.slice(pos)];
  });

  // Drop on a specific row: insert the dragged item BEFORE that row and adopt its group.
  // Handles both reorder-within-group and precise move-between-groups.
  const dropOnRow = (targetRid) => setCatalog((c) => {
    if (!draggedRid || draggedRid === targetRid) return c;
    const dragged = c.find((x) => x._rid === draggedRid);
    const target = c.find((x) => x._rid === targetRid);
    if (!dragged || !target) return c;
    const rest = c.filter((x) => x._rid !== draggedRid);
    const ti = rest.findIndex((x) => x._rid === targetRid);
    return [...rest.slice(0, ti), { ...dragged, group: groupOf(target) }, ...rest.slice(ti)];
  });

  const saveCatalog = async () => {
    setSaving(true);
    try {
      const items = catalog
        .filter((c) => (c.name || "").trim())
        .map((c) => ({ name: c.name, price: c.price, group: groupOf(c) }));
      const { data } = await api.put("/characteristics", { items });
      toast.success(`Catálogo guardado (${data.count} características)`);
      onCatalogSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  // --- referential tables helpers ---
  const setRef = (table, i, field, v) =>
    setRefs((r) => ({ ...r, [table]: r[table].map((it, j) => j === i ? { ...it, [field]: v } : it) }));
  const addRef = (table, tpl) => setRefs((r) => ({ ...r, [table]: [...r[table], { ...tpl }] }));
  const removeRef = (table, i) => setRefs((r) => ({ ...r, [table]: r[table].filter((_, j) => j !== i) }));

  const saveRefs = async () => {
    setSavingRefs(true);
    try {
      await api.put("/settings/references", refs);
      toast.success("Tablas de referencia guardadas");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingRefs(false); }
  };

  const num = (v) => (v === "" || v == null ? null : Number(v));

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 text-xs text-amber-800 flex items-center gap-2">
        <Wrench className="h-4 w-4" /> {globalOnly ? "Catálogo y tablas de referencia" : "Parámetros, catálogo y tablas de referencia"} · solo administradores.
      </div>

      {!globalOnly && <>
      {/* Import params */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-display font-bold text-slate-900 mb-3 text-sm uppercase tracking-wide">Parámetros de importación</h3>
        <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {IMPORT_FIELDS.map(([label, key, step]) => (
            <label key={key} className="block">
              <span className="text-[11px] text-slate-500">{label}</span>
              <div className="mt-1 border border-slate-200 rounded-md">
                <Cell testid={`import-${key}`} step={step} value={importParams?.[key] ?? ""} onChange={(v) => onImport(key, v)} />
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Flete internacional cost */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-display font-bold text-slate-900 mb-1 text-sm uppercase tracking-wide flex items-center gap-2">
          <Ship className="h-4 w-4 text-slate-500" /> Incremento por flete internacional
        </h3>
        <p className="text-[11px] text-slate-500 mb-3">Costo unitario aplicado a la sección Suministro en Precios de venta.</p>
        <label className="block max-w-xs">
          <span className="text-[11px] text-slate-500">Precio flete internacional ($)</span>
          <div className="mt-1 border border-slate-200 rounded-md">
            <Cell testid="flete-precio" step={1} value={solesParams?.flete_precio ?? ""} onChange={(v) => onSolesParam("flete_precio", v)} />
          </div>
        </label>
      </div>
      </>}

      {/* Referential tables */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wide flex items-center gap-2">
              <Table2 className="h-4 w-4 text-slate-500" /> Tablas de referencia
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Visibles para todos los usuarios en Precios de venta y Cotización dólares.</p>
          </div>
          <button data-testid="refs-save" onClick={saveRefs} disabled={savingRefs || !refs}
            className="flex items-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold px-3 py-1.5 rounded-md transition-colors disabled:opacity-60">
            {savingRefs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar tablas
          </button>
        </div>

        {refs === null ? (
          <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Accesorios / cerraduras (tooltip Otros) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Accesorios (tooltip “Otros”)</p>
                <button data-testid="ref-cerr-add" onClick={() => addRef("cerraduras", { label: "", value: 0 })}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"><Plus className="h-3.5 w-3.5" /> Agregar</button>
              </div>
              <div className="space-y-1.5">
                {refs.cerraduras.map((r, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`ref-cerr-${i}`}>
                    <input value={r.label} onChange={(e) => setRef("cerraduras", i, "label", e.target.value)} placeholder="Descripción"
                      className="flex-1 px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
                    <div className="w-28 border border-slate-200 rounded-md"><Cell step={0.5} value={r.value ?? ""} onChange={(v) => setRef("cerraduras", i, "value", num(v))} /></div>
                    <button onClick={() => removeRef("cerraduras", i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Instalación REF PDK */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Instalación · REF PDK conjunto</p>
                <button data-testid="ref-inst-add" onClick={() => addRef("instalacion", { label: "", conjunto: null, pdk: null })}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"><Plus className="h-3.5 w-3.5" /> Agregar</button>
              </div>
              <div className="grid grid-cols-[1fr_6rem_6rem_1.5rem] gap-2 text-[10px] uppercase text-slate-400 px-1 mb-1">
                <span>Tipo</span><span className="text-right">Conjunto</span><span className="text-right">PDK</span><span></span>
              </div>
              <div className="space-y-1.5">
                {refs.instalacion.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_6rem_6rem_1.5rem] gap-2 items-center" data-testid={`ref-inst-${i}`}>
                    <input value={r.label} onChange={(e) => setRef("instalacion", i, "label", e.target.value)} placeholder="Tipo"
                      className="px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
                    <div className="border border-slate-200 rounded-md"><Cell step={1} value={r.conjunto ?? ""} onChange={(v) => setRef("instalacion", i, "conjunto", num(v))} /></div>
                    <div className="border border-slate-200 rounded-md"><Cell step={1} value={r.pdk ?? ""} onChange={(v) => setRef("instalacion", i, "pdk", num(v))} /></div>
                    <button onClick={() => removeRef("instalacion", i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Suministro por dimensión */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Suministro · precios por dimensión</p>
                <button data-testid="ref-sum-add" onClick={() => addRef("suministro", { dimension: "", resto: null, pdk: null })}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"><Plus className="h-3.5 w-3.5" /> Agregar</button>
              </div>
              <div className="grid grid-cols-[1fr_7rem_7rem_1.5rem] gap-2 text-[10px] uppercase text-slate-400 px-1 mb-1">
                <span>Dimensión</span><span className="text-right">$ Resto</span><span className="text-right">$ Solo PDK</span><span></span>
              </div>
              <div className="space-y-1.5">
                {refs.suministro.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_7rem_7rem_1.5rem] gap-2 items-center" data-testid={`ref-sum-${i}`}>
                    <input value={r.dimension} onChange={(e) => setRef("suministro", i, "dimension", e.target.value)} placeholder="Dimensión"
                      className="px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
                    <div className="border border-slate-200 rounded-md"><Cell step={1} value={r.resto ?? ""} onChange={(v) => setRef("suministro", i, "resto", num(v))} /></div>
                    <div className="border border-slate-200 rounded-md"><Cell step={1} value={r.pdk ?? ""} onChange={(v) => setRef("suministro", i, "pdk", num(v))} /></div>
                    <button onClick={() => removeRef("suministro", i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Catalog grouped by section */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wide flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-slate-500" /> Catálogo de características
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Arrastra cada característica al grupo que corresponda. Los cambios aplican solo a cotizaciones NUEVAS.</p>
          </div>
          <button data-testid="catalog-save" onClick={saveCatalog} disabled={saving || !catalog}
            className="flex items-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold px-3 py-1.5 rounded-md transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar todo
          </button>
        </div>
        {catalog === null ? (
          <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>
        ) : (
          <div className="p-4 space-y-4">
            {CHAR_GROUPS.map((g) => {
              const items = catalog.filter((c) => groupOf(c) === g.key);
              return (
                <div key={g.key} data-testid={`catalog-group-${g.key}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (draggedRid) moveToGroup(draggedRid, g.key); setDraggedRid(null); }}
                  className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
                      {g.label} <span className="text-slate-400 font-normal">({items.length})</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <button data-testid={`catalog-add-${g.key}`} onClick={() => addItemToGroup(g.key)}
                        className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                        <Plus className="h-3.5 w-3.5" /> Agregar
                      </button>
                      <button data-testid={`catalog-save-${g.key}`} onClick={saveCatalog} disabled={saving}
                        title="Guarda todo el catálogo (todas las secciones)"
                        className="flex items-center gap-1 text-xs bg-slate-900 hover:bg-slate-800 text-white font-semibold px-2.5 py-1 rounded-md transition-colors disabled:opacity-60">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100 min-h-[46px]">
                    {items.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-slate-400 italic">Arrastra características aquí o presiona “Agregar”.</p>
                    ) : items.map((c, idx) => (
                      <div key={c._rid} draggable
                        onDragStart={() => setDraggedRid(c._rid)} onDragEnd={() => setDraggedRid(null)}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dropOnRow(c._rid); setDraggedRid(null); }}
                        data-testid={`catalog-row-${g.key}-${idx}`}
                        className={`flex items-center gap-2 px-3 py-1.5 ${draggedRid === c._rid ? "opacity-40" : ""}`}>
                        <span className="cursor-grab active:cursor-grabbing shrink-0" title="Arrastra para mover de grupo"
                          data-testid={`catalog-drag-${g.key}-${idx}`}>
                          <GripVertical className="h-4 w-4 text-slate-300" />
                        </span>
                        <input data-testid={`catalog-name-${g.key}-${idx}`} value={c.name}
                          onChange={(e) => setItemById(c._rid, "name", e.target.value)}
                          placeholder="Nombre de la característica"
                          className="flex-1 px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
                        <div className="w-24 border border-slate-200 rounded-md">
                          <Cell testid={`catalog-price-${g.key}-${idx}`} step={0.5} value={c.price} onChange={(v) => setItemById(c._rid, "price", v)} />
                        </div>
                        <button data-testid={`catalog-del-${g.key}-${idx}`} onClick={() => removeItemById(c._rid)} className="text-slate-300 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
